import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Input, Loading, Modal, Pagination, Select, Table, Textarea, Tooltip } from 'react-daisyui';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { useDebouncedCallback } from 'use-debounce';
import * as Yup from 'yup';
import styles from './Cumplimientos.module.scss';
import { Cumplimiento } from './interface';
import { useNavigate, useParams } from 'react-router-dom';

type CumplimientosSchemaType = Yup.InferType<typeof cumplimientosSchema>;

const cumplimientosSchema = Yup.object().shape({
    introduccion: Yup.string()
        .max(1000, 'Maximo 1000 caracteres'),
    cierre: Yup.string()
        .max(1000, 'Maximo 1000 caracteres'),
});

export default function Cumplimientos() {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [saveLoading, setSaveLoading] = useState<boolean>(false);
    const [cumplimientos, setCumplimientos] = useState<Array<Cumplimiento>>([]);
    const [cumplimiento, setCumplimiento] = useState<Cumplimiento | null>(null);

    const [nombre, setNombre] = useState<string>('');
    const [referencia, setReferencia] = useState<string>('');
    const [introduccion, setIntroduccion] = useState<string>('');
    const [cierre, setCierre] = useState<string>('');
    const [search, setSearch] = useState<string>('');
    const [total, setTotal] = useState(0)
    const [limit, setLimit] = useState(20)
    const [page, setPage] = useState(1)
    const { cuestionarioId } = useParams()
    const navigate = useNavigate();

    const detailModal = useRef<HTMLDialogElement>(null);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm<CumplimientosSchemaType>({
        resolver: yupResolver(cumplimientosSchema)
    });

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const maxPageButtons = 5;
        const totalPages = Math.ceil(total / limit);
        const maxPage = Math.min(totalPages, maxPageButtons);
        const middlePage = Math.ceil(maxPage / 2);
        const startPage = Math.max(1, page - middlePage);
        const endPage = Math.min(totalPages, startPage + maxPage - 1);

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(
                <Button size="sm" key={i} onClick={() => setPage(i)} className={["join-item"].join(' ')} active={i === page}>
                    {i}
                </Button>
            );
        }

        return pageNumbers;
    }

    const descargarCumplimientos = async () => {
        setSaveLoading(true);
        axios.get(`/api/cuestionarios/${cuestionarioId}/cumplimientos?offset=` + (page - 1) * limit + "&limit=" + limit + (search ? "&filters[search]=" + search : '')).then((response) => {
            setCumplimientos(response.data.data);
            setTotal(response.data.total);
        }).finally(() => {
            setLoading(false);
        });
    }

    useEffect(() => {
        reset();
        setNombre('');
        setReferencia('');
        setIntroduccion('');
        setCierre('');
        descargarCumplimientos();
    }, [limit, page, search, open]);

    const save = async () => {
        axios(`/api/cuestionarios/${cuestionarioId}/cumplimientos${cumplimiento && cumplimiento.id_cuestionario_nivel_cumplimiento ? `/${cumplimiento.id_nivel_cumplimiento}` : ''}`, {
            method: cumplimiento && cumplimiento.id_cuestionario_nivel_cumplimiento ? 'PUT' : 'POST',
            data: {
                ...cumplimiento,
                //nombre,
                //referencia,
                introduccion,
                cierre
            }
        }).then(() => {
            Swal.fire({
                heightAuto: false,
                icon: 'success',
                title: '¡Listo!',
                text: 'La cumplimiento se ha guardado correctamente'
            });
        }).catch((e) =>
            Swal.fire({
                heightAuto: false,
                icon: 'error',
                title: 'Error al guardar la cumplimiento',
                text: e.response.data.message
            }))
            .finally(() => {
                setSaveLoading(false);

                detailModal.current?.close();
                setOpen(false);
                setCumplimiento(null);
                setNombre('');
                setReferencia('');
                setIntroduccion('');
                setCierre('');
                descargarCumplimientos();
            });
    }

    const editarCumplimiento = (e: Cumplimiento) => {
        setCumplimiento(e);
        detailModal.current?.showModal();
        setNombre(e.nombre || '');
        setReferencia(e.referencia || '');
        setIntroduccion(e.introduccion || '');
        setCierre(e.cierre || '');
    }

    const debounced = useDebouncedCallback((value) => {
        setSearch(value);
    }, 500);

    const colorCumplimiento = (cumplimiento: Cumplimiento) => {
        switch (String(cumplimiento.id_nivel_cumplimiento)) {
            case '1':
                return 'error';
            case '2':
                return 'warning';
            case '3':
                return 'success';
            default:
                return 'accent';
        }
    };

    return (
        <>
            <div className={styles.cumplimientos_wrapper}>
                <div className={styles.actions}>
                    <Button onClick={() => navigate(-1)} color="ghost" size='sm'>
                        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="m313-440 196 196q12 12 11.5 28T508-188q-12 11-28 11.5T452-188L188-452q-6-6-8.5-13t-2.5-15q0-8 2.5-15t8.5-13l264-264q11-11 27.5-11t28.5 11q12 12 12 28.5T508-715L313-520h447q17 0 28.5 11.5T800-480q0 17-11.5 28.5T760-440H313Z" /></svg>
                    </Button>
                    <Input className='w-full' placeholder="Buscar" size="sm" onChange={(e) => debounced(e.target.value)} />
                </div>
                <div className="h-full" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
                    <div className="">
                        {loading ? <div className={styles.loading}><Loading color="primary" /></div> :
                            <Table zebra pinRows>
                                <Table.Head className="th_acciones">
                                    <span>Nombre</span>
                                    <span>Referencia</span>
                                    <span>Introduccion</span>
                                    <span>Cierre</span>
                                    <span>Acciones</span>
                                </Table.Head>

                                <Table.Body>
                                    {cumplimientos.map((e, item) => (
                                        <Table.Row key={item} hover>
                                            <span><Badge color={colorCumplimiento(e) ?? 'accent'}>{e.nombre}</Badge> </span>
                                            <span>{e.referencia}</span>
                                            <span className='line-clamp'>{e.introduccion}</span>
                                            <span className='line-clamp'>{e.cierre}</span>
                                            <span>
                                                <div className="flex space-x-2 items-center gap-2">
                                                    <Tooltip message="Editar">
                                                        <Button size="sm" shape="square" color="secondary" onClick={() => editarCumplimiento(e)}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px"><path d="M200-200h57l391-391-57-57-391 391v57Zm-40 80q-17 0-28.5-11.5T120-160v-97q0-16 6-30.5t17-25.5l505-504q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L313-143q-11 11-25.5 17t-30.5 6h-97Zm600-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z" /></svg>
                                                        </Button>
                                                    </Tooltip>
                                                </div>
                                            </span>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        }
                    </div>
                </div>

                <div className={styles.pagination}>
                    <div className={styles.cantidad}>
                        <small >Cantidad por página:</small>
                        <Select size="sm" id="itemsPerPage" value={limit} onChange={(e) => setLimit(parseInt(e.target.value))} >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                        </Select>
                    </div>
                    <Pagination className="flex justify-center">
                        <Button size="sm" onClick={() => setPage(page - 1)} disabled={page === 1} className="join-item">
                            Anterior
                        </Button>
                        {renderPageNumbers()}
                        <Button size="sm" onClick={() => setPage(page + 1)} disabled={page === Math.ceil(total / limit)} className="join-item">
                            Siguiente
                        </Button>
                    </Pagination>
                </div>
                <Modal open={open} ref={detailModal}>
                    <form method="dialog">
                        <Button size="sm" color="ghost" shape="circle" className="absolute" style={{ top: '16px', right: '16px' }} onClick={() => setOpen(false)}>
                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M480-424 284-228q-11 11-28 11t-28-11q-11-11-11-28t11-28l196-196-196-196q-11-11-11-28t11-28q11-11 28-11t28 11l196 196 196-196q11-11 28-11t28 11q11 11 11 28t-11 28L536-480l196 196q11 11 11 28t-11 28q-11 11-28 11t-28-11L480-424Z" /></svg>
                        </Button>
                    </form>
                    <Modal.Header className="font-bold">Nueva cumplimiento</Modal.Header>
                    <Modal.Body>
                        {!saveLoading ? <Loading color='primary' /> : <>
                            <form onSubmit={handleSubmit(save)}>
                                <div className="form-control mb-2">
                                    <label className="label">
                                        <span className="label-text">Nombre</span>
                                    </label>
                                    <Input
                                        type="text"
                                        placeholder="Nombre"
                                        className="input input-bordered"
                                        value={nombre}
                                        onChange={(e) => setNombre(e.target.value)}
                                        disabled
                                    />
                                </div>
                                <div className="form-control mb-2">
                                    <label className="label">
                                        <span className="label-text">Referencia</span>
                                    </label>
                                    <input type="text" placeholder="Referencia" className="input input-bordered" onChange={(e) => setReferencia(e.target.value)} value={referencia} disabled />
                                </div>
                                <div className="form-control mb-2">
                                    <label className="label">
                                        <span className="label-text">Introduccion</span>
                                    </label>
                                    <Textarea color={errors.introduccion ? 'error' : 'neutral'} {...register('introduccion')} placeholder="Introduccion" className="input input-bordered" onChange={(e) => setIntroduccion(e.target.value)} value={introduccion} />
                                    {errors.introduccion && <span className="label-text-alt text-error">{errors.introduccion.message}</span>}
                                </div>
                                <div className="form-control mb-2">
                                    <label className="label">
                                        <span className="label-text">Cierre</span>
                                    </label>
                                    <Textarea color={errors.cierre ? 'error' : 'neutral'} {...register('cierre')} placeholder="Cierre" className="input input-bordered" onChange={(e) => setCierre(e.target.value)} value={cierre} />
                                    {errors.cierre && <span className="label-text-alt text-error">{errors.cierre.message}</span>}
                                </div>
                            </form>
                        </>}
                    </Modal.Body>
                    <Modal.Actions>
                        <Button color="primary" fullWidth onClick={handleSubmit(save)}>Guardar</Button>
                    </Modal.Actions>
                </Modal>
            </div>
        </>
    );
}
